// The column preferences of the kit DataTable: which columns show, in which
// order, and at which width (SEP-298).
//
// A column shows by default, unless it has `defaultHidden`. The reader can
// hide a column or show a hidden one, move a column, and resize a column. The
// preference keeps only the changes from the default, so a column that a page
// adds later takes its own default. A column that a page removes drops out of
// each list when the table reads the preference.
// Pure: no React and no storage, so the rules are unit-tested here.

export interface ChooserColumn {
    id: string;
    /** false = the reader cannot hide this column. The default is true. */
    hideable?: boolean;
    /** true = the column is available but does not show until the reader adds it. */
    defaultHidden?: boolean;
    /** false = the column keeps its default position. A row action or a status
     *  column with no header text cannot move, so the reader cannot lose it.
     *  The default is true. */
    movable?: boolean;
}

/** The changes of one reader from the default columns of a table. */
export interface ColumnPrefs {
    /** Columns that show by default and that the reader hid. */
    hidden: readonly string[];
    /** Columns that are hidden by default and that the reader added. */
    shown: readonly string[];
    /** The order of the movable columns that the reader chose. Empty = the
     *  default order. */
    order?: readonly string[];
    /** The width in pixels of each column that the reader resized. */
    widths?: Readonly<Record<string, number>>;
}

export const EMPTY_COLUMN_PREFS: ColumnPrefs = { hidden: [], shown: [], order: [], widths: {} };

/** The smallest width that a stored width can give a column. */
export const MIN_STORED_WIDTH_PX = 24;

function shownByDefault(col: ChooserColumn): boolean {
    return !col.defaultHidden || col.hideable === false;
}

function isMovable(col: ChooserColumn): boolean {
    return col.movable !== false;
}

/**
 * The ids of all the columns, in the order of the reader. A column that
 * cannot move keeps its default index. The movable columns fill the other
 * places in the stored order. A movable column that the stored order does not
 * know (a column that a page added later) goes after the column that comes
 * before it in the default order.
 */
export function orderedColumnIds(columns: readonly ChooserColumn[], prefs: ColumnPrefs): string[] {
    const movable = columns.filter(isMovable).map((c) => c.id);
    const known = new Set(movable);
    const stored = (prefs.order ?? []).filter(
        (id, i, all) => known.has(id) && all.indexOf(id) === i,
    );
    let sequence: string[];
    if (stored.length === 0) {
        sequence = movable;
    } else {
        sequence = [...stored];
        const placed = new Set(stored);
        movable.forEach((id, i) => {
            if (placed.has(id)) return;
            // The nearest column before it in the default order that has a place.
            let at = -1;
            for (let j = i - 1; j >= 0 && at < 0; j--) at = sequence.indexOf(movable[j]);
            sequence.splice(at + 1, 0, id);
            placed.add(id);
        });
    }
    let next = 0;
    return columns.map((col) => (isMovable(col) ? sequence[next++] : col.id));
}

/** The ids of the columns that show, in the order of the reader. At least one shows. */
export function visibleColumnIds(columns: readonly ChooserColumn[], prefs: ColumnPrefs): string[] {
    const hidden = new Set(prefs.hidden);
    const shown = new Set(prefs.shown);
    const byId = new Map(columns.map((c) => [c.id, c]));
    const ids = orderedColumnIds(columns, prefs).filter((id) => {
        const col = byId.get(id)!;
        if (col.hideable === false) return true;
        return shownByDefault(col) ? !hidden.has(col.id) : shown.has(col.id);
    });
    // A table with no column cannot be read, and a stale preference must not
    // make one. The first column comes back.
    if (ids.length === 0 && columns.length > 0) return [columns[0].id];
    return ids;
}

/**
 * Move one column to the place of another column. The moved column goes
 * before the target when it moves to the left, and after the target when it
 * moves to the right. A column that cannot move, or a target that cannot
 * move, gives no change.
 */
export function moveColumn(
    columns: readonly ChooserColumn[],
    prefs: ColumnPrefs,
    id: string,
    targetId: string,
): ColumnPrefs {
    if (id === targetId) return prefs;
    const byId = new Map(columns.map((c) => [c.id, c]));
    const col = byId.get(id);
    const target = byId.get(targetId);
    if (!col || !target || !isMovable(col) || !isMovable(target)) return prefs;
    const current = orderedColumnIds(columns, prefs).filter((x) => isMovable(byId.get(x)!));
    const from = current.indexOf(id);
    const to = current.indexOf(targetId);
    const next = current.filter((x) => x !== id);
    next.splice(next.indexOf(targetId) + (from < to ? 1 : 0), 0, id);
    const isDefault = next.every((x, i) => x === columns.filter(isMovable)[i]?.id);
    return { ...prefs, order: isDefault ? [] : next };
}

/**
 * Move one column one place to the left (-1) or to the right (+1), past the
 * next visible column that can move. A hidden column does not count, so each
 * key press gives a change that the reader can see.
 */
export function moveColumnBy(
    columns: readonly ChooserColumn[],
    prefs: ColumnPrefs,
    id: string,
    step: -1 | 1,
): ColumnPrefs {
    const byId = new Map(columns.map((c) => [c.id, c]));
    const visible = visibleColumnIds(columns, prefs).filter((x) => isMovable(byId.get(x)!));
    const at = visible.indexOf(id);
    if (at < 0) return prefs;
    const target = visible[at + step];
    return target === undefined ? prefs : moveColumn(columns, prefs, id, target);
}

/** Keep the width that the reader gave one column. */
export function setColumnWidth(prefs: ColumnPrefs, id: string, width: number): ColumnPrefs {
    if (!Number.isFinite(width)) return prefs;
    const px = Math.max(MIN_STORED_WIDTH_PX, Math.round(width));
    if (prefs.widths?.[id] === px) return prefs;
    return { ...prefs, widths: { ...prefs.widths, [id]: px } };
}

/** The stored width of a column that the table still has, or undefined. */
export function storedColumnWidth(prefs: ColumnPrefs, id: string): number | undefined {
    return prefs.widths?.[id];
}

/** True when the reader changed nothing: no column shown, hidden, moved or resized. */
export function isEmptyColumnPrefs(prefs: ColumnPrefs): boolean {
    return (
        prefs.hidden.length === 0 &&
        prefs.shown.length === 0 &&
        (prefs.order ?? []).length === 0 &&
        Object.keys(prefs.widths ?? {}).length === 0
    );
}

/** Show or hide one column. The last visible column cannot be hidden. */
export function toggleColumn(
    columns: readonly ChooserColumn[],
    prefs: ColumnPrefs,
    id: string,
): ColumnPrefs {
    const col = columns.find((c) => c.id === id);
    if (!col || col.hideable === false) return prefs;
    const visible = visibleColumnIds(columns, prefs);
    const isVisible = visible.includes(id);
    if (isVisible && visible.length === 1) return prefs;
    const without = (list: readonly string[]) => list.filter((x) => x !== id);
    if (shownByDefault(col)) {
        return isVisible
            ? { ...prefs, hidden: [...prefs.hidden, id] }
            : { ...prefs, hidden: without(prefs.hidden) };
    }
    return isVisible
        ? { ...prefs, shown: without(prefs.shown) }
        : { ...prefs, shown: [...prefs.shown, id] };
}

/** Show every column. The order and the widths stay. */
export function showAllColumns(
    columns: readonly ChooserColumn[],
    prefs?: ColumnPrefs,
): ColumnPrefs {
    return {
        ...prefs,
        hidden: [],
        shown: columns.filter((c) => c.defaultHidden && c.hideable !== false).map((c) => c.id),
    };
}

/**
 * True when the table shows its default layout: the default columns, in the
 * default order, at the widths that the table measures.
 */
export function isDefaultColumns(columns: readonly ChooserColumn[], prefs: ColumnPrefs): boolean {
    const a = visibleColumnIds(columns, prefs).join('\u0000');
    const b = visibleColumnIds(columns, EMPTY_COLUMN_PREFS).join('\u0000');
    const ids = new Set(columns.map((c) => c.id));
    const resized = Object.keys(prefs.widths ?? {}).some((id) => ids.has(id));
    return a === b && !resized;
}

/** Read a stored preference. Anything that is not valid gives the default. */
export function parseColumnPrefs(raw: string | null): ColumnPrefs {
    if (!raw) return EMPTY_COLUMN_PREFS;
    try {
        const value: unknown = JSON.parse(raw);
        if (typeof value !== 'object' || value === null) return EMPTY_COLUMN_PREFS;
        const { hidden, shown, order, widths } = value as {
            hidden?: unknown;
            shown?: unknown;
            order?: unknown;
            widths?: unknown;
        };
        const strings = (list: unknown) =>
            Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [];
        // A record from before SEP-298 has no order and no widths.
        const sizes: Record<string, number> = {};
        if (typeof widths === 'object' && widths !== null && !Array.isArray(widths)) {
            for (const [id, px] of Object.entries(widths)) {
                if (typeof px === 'number' && Number.isFinite(px) && px > 0) {
                    sizes[id] = Math.max(MIN_STORED_WIDTH_PX, Math.round(px));
                }
            }
        }
        return {
            hidden: strings(hidden),
            shown: strings(shown),
            order: strings(order),
            widths: sizes,
        };
    } catch {
        return EMPTY_COLUMN_PREFS;
    }
}

export function serialiseColumnPrefs(prefs: ColumnPrefs): string {
    return JSON.stringify({
        hidden: prefs.hidden,
        shown: prefs.shown,
        order: prefs.order ?? [],
        widths: prefs.widths ?? {},
    });
}

/**
 * The storage key of one table for one user. The key holds the user id, so
 * two staff users who share one browser do not share a layout. With no user
 * (the session is not known yet), the key is the key of the browser only.
 */
export function columnPrefsStorageKey(tableKey: string, userId?: string | null): string {
    return userId
        ? `septima.table-columns:${userId}:${tableKey}`
        : `septima.table-columns:${tableKey}`;
}

/**
 * The default key of a table: the route with its record ids replaced, and the
 * label of the table with its numbers replaced. Many labels carry a row count,
 * for example "Contacts (12)", and a key that changes with the count loses
 * the choice of the reader. The route keeps two tables with the same label on
 * different pages apart. The same page for two records shares one choice.
 */
export function defaultColumnsKey(pathname: string, ariaLabel: string): string {
    const route = pathname
        .split('/')
        .map((part) => (/\d/.test(part) ? ':id' : part))
        .join('/');
    const label = ariaLabel.replace(/\d[\d,.]*/g, '#');
    return `${route}|${label}`;
}
